import React, { useEffect, useState } from 'react';
import {
    Drawer,
    Tabs,
    Descriptions,
    Tag,
    DatePicker,
    Spin,
    Button,
    notification,
    Typography,
    InputNumber,
    Form,
} from 'antd';
import { useCurrentRegisterTaxiDriver } from '../../hooks/useCurrentRegisterTaxiDriver';
import {
    CloseCircleOutlined,
    CheckCircleOutlined,
    NotificationOutlined,
    EditOutlined,
    CheckOutlined,
} from '@ant-design/icons';
import JobsHistory from './JobsHistory';
import { useGetTripsAndEarningsTaxiDriverQuery } from '../../../../GraphQL/hooks/useGetTripsAndEarningsTaxiDriverQuery';
import dayjs from 'dayjs';
import { Line, Column } from '@ant-design/plots';
import { useGetRewardedDriversQuery } from '../../../../GraphQL/hooks/useGetRewardedDriversQuery';
import { useNotifyDriverReferralRewardMutation } from '../../../../GraphQL/hooks/useNotifyDriverReferralRewardMutation';
import { RewardStatus } from '../../../../shared/types';
import { useUpdateStatusDriverMutation } from '../../../../GraphQL/hooks/useUpdateStatusDriverMutation';

const { RangePicker } = DatePicker;

const TripStatisticCard = ({ icon, label, count, color = '#1677ff' }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
            borderRadius: '10px',
            padding: '12px 16px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            marginBottom: '12px',
            maxWidth: '250px',
        }}
    >
        <div style={{ fontSize: 32, color: color, marginRight: 12 }}>{icon}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 500 }}>{label}</div>
            <div style={{ fontSize: '16px', fontWeight: 600, color }}>{count}</div>
        </div>
    </div>
);

const DriverDetailsDrawer = ({ open, onClose, updateList }) => {
    const { currentRegisterTaxiDriver } = useCurrentRegisterTaxiDriver();
    if (!currentRegisterTaxiDriver) return null;

    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf('month'),
        dayjs().endOf('month'),
    ]);

    const [totalRides, setTotalRides] = useState(0);
    const [totalEarnings, setTotalEarnings] = useState(0);
    const [commissionAmount, setCommissionAmount] = useState(0);
    const [totalCanceledJobs, setTotalCanceledJobs] = useState(0);
    const [isDriverNotified, setDriverNotified] = useState(false);
    const [activeTab, setActiveTab] = useState('1');

    const [rewardStatus, setRewardStatus] = useState<RewardStatus>(RewardStatus.NotEligible);
    const [editingCommission, setEditingCommission] = useState(false);
    const [commissionValue, setCommissionValue] = useState(currentRegisterTaxiDriver?.commissionRate);

    const [stats, setStats] = useState({
        numberRides: 0,
        numberCanceledOffers: 0,
        earningSummary: 0,
        bonus: 0,
        commission: 0,
        commissionAmount: 0,
    });

    useEffect(() => {
        if (!open) {
            setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
            setTotalRides(0);
            setTotalEarnings(0);
            setCommissionAmount(0);
            setTotalCanceledJobs(0);
            setDriverNotified(false);
            setRewardStatus(RewardStatus.NotEligible);
            setEditingCommission(false);
            setCommissionValue(currentRegisterTaxiDriver?.commissionRate);
            setActiveTab('1');
        }
    }, [open, currentRegisterTaxiDriver]);

    const { data: EarningsTrends, loading: loadingEarningsTrends } = useGetTripsAndEarningsTaxiDriverQuery({
        driverId: currentRegisterTaxiDriver?._id,
        startDate: dateRange[0].toDate(),
        endDate: dateRange[1].toDate(),
    });

    const { mutation, loading: creating } = useUpdateStatusDriverMutation();
    const { notifyDriverReward, loading: loadingNotifyDriverReward } = useNotifyDriverReferralRewardMutation();
    const { data: rewardedDrivers, refetch } = useGetRewardedDriversQuery();

    useEffect(() => {
        if (EarningsTrends && currentRegisterTaxiDriver?._id) {
            // Store total rides and total earnings only once
            if (totalRides === 0) {
                setTotalRides(EarningsTrends.numberRides);
            }
            if (totalCanceledJobs === 0) {
                setTotalCanceledJobs(EarningsTrends.numberCanceledOffers);
            }
            if (totalEarnings === 0) {
                setTotalEarnings(EarningsTrends.earningSummary);
            }
            if (commissionAmount === 0) {
                setCommissionAmount(EarningsTrends.commissionAmount);
            }
            setStats({
                numberRides: EarningsTrends.numberRides ?? totalRides, // keep total
                numberCanceledOffers: EarningsTrends.numberCanceledOffers ?? totalCanceledJobs, // keep total
                earningSummary: EarningsTrends.earningSummary ?? totalEarnings,
                commissionAmount: EarningsTrends.commissionAmount ?? commissionAmount,
                bonus: EarningsTrends.bonus,
                commission: EarningsTrends.commissionRate,
            });
        }
    }, [
        EarningsTrends,
        currentRegisterTaxiDriver?._id,
        totalRides,
        totalEarnings,
        totalCanceledJobs,
        commissionAmount,
    ]);

    useEffect(() => {
        if (currentRegisterTaxiDriver) {
            setCommissionValue(currentRegisterTaxiDriver.commissionRate || 0);
        }
    }, [currentRegisterTaxiDriver]);

    useEffect(() => {
        if (!currentRegisterTaxiDriver || !rewardedDrivers) return;

        const eligibility = rewardedDrivers.find((d) => d._id === currentRegisterTaxiDriver._id);

        const eligibleCount = eligibility?.eligibleReferralCount ?? 0;
        const rewardedCount = currentRegisterTaxiDriver.rewardedReferralCount ?? 0;
        const isRewardedReferral = currentRegisterTaxiDriver.isRewardedReferral;

        if (eligibleCount > 0) {
            setRewardStatus(RewardStatus.Eligible);
        } else if (isRewardedReferral) {
            setRewardStatus(RewardStatus.Notified);
        } else if (rewardedCount > 0) {
            setRewardStatus(RewardStatus.Rewarded);
        } else {
            setRewardStatus(RewardStatus.NotEligible);
        }
    }, [rewardedDrivers]);

    const handleUpdateCommissionDriver = async () => {
        try {
            await mutation({
                _id: currentRegisterTaxiDriver._id,
                commissionRate: commissionValue,
            })
                .then(() => {
                    setEditingCommission(false);
                    updateList();
                })
                .finally(() => {
                    notification.success({
                        message: 'Driver commission updated successfully',
                        description: 'The driver commission has been updated successfully.',
                    });
                })
                .catch((err) => {
                    notification.error({
                        message: 'Failed to update driver commission',
                        description: 'An error occurred while updating the driver commission.',
                    });
                    console.error('Failed to update driver commission:', err);
                });
        } catch (error) {
            notification.error({
                message: 'Driver commission updated Failed',
                description: 'Unable to update the commission of the driver. Please try again later.',
            });
        }
    };

    const handleNotifyDriverReward = async () => {
        try {
            const responseMsg = await notifyDriverReward(currentRegisterTaxiDriver._id);
            notification.success({
                message: 'Driver Notified',
                description: responseMsg || 'The driver has been notified successfully!',
            });
            updateList();
            setDriverNotified(true);
        } catch (err) {
            console.error(err);

            notification.error({
                message: 'Notification Failed',
                description: 'Unable to notify the driver. Please try again later.',
            });
        }
    };

    return (
        <Drawer
            title={`Driver Details - ${currentRegisterTaxiDriver.fullName}`}
            placement="right"
            width={700}
            onClose={onClose}
            open={open}
        >
            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                items={[
                    {
                        key: '1',
                        label: 'Profile',
                        children: (
                            <div>
                                {/* Date Range Filter */}
                                <div style={{ marginBottom: 16 }}>
                                    <RangePicker
                                        value={dateRange}
                                        onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                                    />
                                </div>

                                {/* Jobs Statistics */}
                                {loadingEarningsTrends ? (
                                    <Spin />
                                ) : (
                                    <>
                                        <h3 style={{ marginBottom: '16px' }}>Job Statistics</h3>
                                        <TripStatisticCard
                                            icon={<CheckCircleOutlined />}
                                            label="Total Jobs"
                                            count={stats.numberRides}
                                            color="#52c41a"
                                        />
                                        <TripStatisticCard
                                            icon={<CloseCircleOutlined />}
                                            label="Cancelled Jobs"
                                            count={stats.numberCanceledOffers}
                                            color="#ff4d4f"
                                        />
                                        <div style={{ display: 'flex', gap: 24, marginTop: 24 }}>
                                            {/* Jobs Column Chart */}
                                            <div style={{ flex: 1 }}>
                                                <h4>Jobs Overview</h4>
                                                <Column
                                                    data={[
                                                        { type: 'Completed Jobs', value: stats.numberRides },
                                                        { type: 'Cancelled Jobs', value: stats.numberCanceledOffers },
                                                    ]}
                                                    xField="type"
                                                    yField="value"
                                                    yAxis={{
                                                        min: 0,
                                                        max:
                                                            Math.max(stats.numberRides, stats.numberCanceledOffers) *
                                                            1.2,
                                                    }}
                                                    columnWidthRatio={0.4}
                                                    color={({ type }) =>
                                                        type === 'Cancelled Jobs' ? '#ff4d4f' : '#52c41a'
                                                    }
                                                    label={{
                                                        position: 'middle',
                                                        style: { fill: '#fff' },
                                                    }}
                                                    height={200}
                                                />
                                            </div>

                                            {/* Earnings Line Chart */}
                                            <div style={{ flex: 1 }}>
                                                <h4>Earnings Over Time</h4>
                                                <Line
                                                    data={EarningsTrends?.weeklyTrips || []}
                                                    xField="label"
                                                    yField="value"
                                                    smooth
                                                    point={{ size: 3, shape: 'circle' }}
                                                    color="#1677ff"
                                                    height={200}
                                                />
                                            </div>
                                        </div>
                                        {/* Driver Information */}
                                        <Descriptions
                                            title="Driver Information"
                                            bordered
                                            column={1}
                                            size="small"
                                            style={{
                                                background: '#fff',
                                                borderRadius: '8px',
                                                marginTop: '20px',
                                                padding: '12px',
                                            }}
                                        >
                                            <Descriptions.Item label="ID Card">
                                                <Tag color="blue">{currentRegisterTaxiDriver.idCard}</Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Base Subscription">
                                                <Tag color="blue">{currentRegisterTaxiDriver.monthlyFee} TND</Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Earning Summary">
                                                <Tag color="green">{stats.earningSummary} TND</Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Bonus">
                                                <Tag color="gold">{stats.bonus} TND</Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Work Plan and Commission">
                                                <Tag color="blue">{currentRegisterTaxiDriver.workRangeKm} km range</Tag>
                                                {editingCommission ? (
                                                    <Tag color="orange">
                                                        <InputNumber
                                                            size="small"
                                                            min={0}
                                                            max={100}
                                                            value={commissionValue}
                                                            onChange={(v) => setCommissionValue(v)}
                                                            style={{ width: 70 }}
                                                        />
                                                        %
                                                        <CheckOutlined
                                                            style={{ marginLeft: 6, cursor: 'pointer' }}
                                                            onClick={handleUpdateCommissionDriver}
                                                        />
                                                    </Tag>
                                                ) : (
                                                    <Tag color="red">
                                                        {commissionValue} % commission
                                                        <EditOutlined
                                                            style={{ marginLeft: 6, cursor: 'pointer' }}
                                                            onClick={() => setEditingCommission(true)}
                                                        />
                                                    </Tag>
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Commission Amount">
                                                <Tag color="gold">{stats.commissionAmount?.toFixed(2)} TND</Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Used Referral code">
                                                <Tag color="blue">{currentRegisterTaxiDriver.usedReferralCode}</Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Reward Status (Referral Code Used)">
                                                {rewardStatus === RewardStatus.NotEligible && (
                                                    <Tag color="default">Not Eligible</Tag>
                                                )}

                                                {rewardStatus === RewardStatus.Eligible && (
                                                    <>
                                                        <Tag color="gold">Eligible for Reward 🎉</Tag>
                                                        <Button
                                                            type="primary"
                                                            shape="round"
                                                            size="small"
                                                            loading={loadingNotifyDriverReward}
                                                            disabled={isDriverNotified}
                                                            onClick={handleNotifyDriverReward}
                                                            icon={<NotificationOutlined />}
                                                            style={{ padding: '0 3 0 3' }}
                                                        >
                                                            Notify
                                                        </Button>
                                                    </>
                                                )}
                                                {rewardStatus === RewardStatus.Notified && (
                                                    <>
                                                        <Tag color="processing">The driver was notified</Tag>
                                                        <Button
                                                            type="primary"
                                                            shape="round"
                                                            size="small"
                                                            loading={loadingNotifyDriverReward}
                                                            disabled={!isDriverNotified}
                                                            icon={<NotificationOutlined />}
                                                            style={{ padding: '0 3 0 3' }}
                                                        >
                                                            Notify
                                                        </Button>
                                                    </>
                                                )}
                                                {rewardStatus === RewardStatus.Rewarded && (
                                                    <>
                                                        <Tag color="green">Rewarded Previously</Tag>
                                                        <br />
                                                        <Typography.Text>
                                                            This driver has already received a referral reward{' '}
                                                            {currentRegisterTaxiDriver?.rewardedReferralCount} time(s)
                                                        </Typography.Text>
                                                    </>
                                                )}
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </>
                                )}
                            </div>
                        ),
                    },
                    {
                        key: '2',
                        label: 'Jobs History',
                        children: (
                            <JobsHistory
                                driverId={currentRegisterTaxiDriver?._id}
                                open={open}
                                handleViewInvoice={(trip) => {}}
                            />
                        ),
                    },
                ]}
            />
        </Drawer>
    );
};

export default DriverDetailsDrawer;
